import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Star, Clock, Truck, Leaf, ShieldCheck, Minus, Plus, ArrowLeft, ShoppingBag } from "lucide-react";
import { FLAVORS, WEIGHTS, TESTIMONIALS } from "../mock";
import { fetchProduct, fetchProducts } from "../lib/api";
import { useCart } from "../context/CartContext";
import ProductCard from "../components/ProductCard";
import Seo from "../components/Seo";
import { inr } from "../lib/format";

const ProductDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem, setIsOpen } = useCart();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  const [flavor, setFlavor] = useState(FLAVORS[0]);
  const [weight, setWeight] = useState(WEIGHTS[1]);
  const [qty, setQty] = useState(1);
  const [message, setMessage] = useState("");
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setActiveImg(0);
    fetchProduct(slug)
      .then((p) => {
        if (!active) return;
        setProduct(p);
        setFlavor(p.flavor || FLAVORS[0]);
        setWeight(p.weight || WEIGHTS[1]);
        const cat = p.categories?.[0];
        fetchProducts(cat).then((list) => {
          if (active) setRelated(list.filter((x) => x.id !== p.id).slice(0, 4));
        });
      })
      .catch(() => active && setProduct(null))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [slug]);

  if (loading) {
    return <div className="max-w-7xl mx-auto px-4 py-24 text-center text-[#B07A5C]">Loading cake...</div>;
  }

  if (!product) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-24 text-center">
        <p className="text-[#6B4226] text-lg">Cake not found.</p>
        <Link to="/collections/all" className="text-[#D46A8A] mt-3 inline-block">Browse all cakes</Link>
      </div>
    );
  }

  const weightMultiplier = { "0.5 Kg": 0.65, "1 Kg": 1, "1.5 Kg": 1.45, "2 Kg": 1.9, "3 Kg": 2.75 };
  const unitPrice = Math.round(product.price * (weightMultiplier[weight] || 1));
  const gallery = product.gallery?.length ? product.gallery : [product.image];

  const handleAdd = () => addItem(product, { flavor, weight, qty, message });
  const handleBuyNow = () => { addItem(product, { flavor, weight, qty, message }); setIsOpen(false); navigate("/checkout"); };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
      <Seo
        path={`/products/${product.slug}`}
        title={`${product.name} — Online Cake Delivery in Noida`}
        description={`Order ${product.name} online from Globaltreats. ${product.flavor} · ${product.weight}. Freshly baked with next-day delivery across Noida, Greater Noida & Indirapuram.`}
        image={product.image}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Product",
          name: product.name,
          image: product.image,
          description: product.description || `${product.name} by Globaltreats, freshly baked and hand-decorated. Delivered across Noida & Greater Noida.`,
          brand: { "@type": "Brand", name: "Globaltreats" },
          offers: {
            "@type": "Offer",
            priceCurrency: "INR",
            price: product.price,
            availability: "https://schema.org/InStock",
            url: `https://globaltreats.in/products/${product.slug}`,
            areaServed: "Noida, Greater Noida, Indirapuram",
          },
          aggregateRating: { "@type": "AggregateRating", ratingValue: product.rating || 4.9, reviewCount: product.reviews || 12 },
        }}
      />
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-sm text-[#B07A5C] hover:text-[#D46A8A] mb-6"><ArrowLeft size={16} /> Back</button>

      <div className="grid md:grid-cols-2 gap-10">
        {/* Gallery */}
        <div>
          <div className="rounded-[2rem] overflow-hidden bakery-shadow aspect-square bg-[#F6E7DA]">
            <img src={gallery[activeImg]} alt={product.name} className="w-full h-full object-cover" />
          </div>
          {gallery.length > 1 && (
            <div className="flex gap-3 mt-4">
              {gallery.map((g, i) => (
                <button key={i} onClick={() => setActiveImg(i)} className={`w-20 h-20 rounded-xl overflow-hidden border-2 ${activeImg === i ? "border-[#D46A8A]" : "border-transparent"}`}>
                  <img src={g} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            {product.bestseller && <span className="bg-[#D46A8A] text-white text-xs font-semibold px-2.5 py-1 rounded-full">Bestseller</span>}
            <span className="text-xs text-[#B07A5C]">SKU: {product.id}</span>
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-[#3A2417]">{product.name}</h1>
          <div className="flex items-center gap-2 mt-2">
            <div className="flex gap-0.5">{Array.from({ length: 5 }).map((_, i) => <Star key={i} size={16} className={i < Math.round(product.rating) ? "fill-[#E8A6B8] text-[#E8A6B8]" : "text-[#EADBCB]"} />)}</div>
            <span className="text-sm text-[#6B4226]">{product.rating} · {product.reviews} reviews</span>
          </div>

          <div className="flex items-center gap-3 mt-4">
            <span className="font-display text-3xl font-bold text-[#3A2417]">{inr(unitPrice * qty)}</span>
            {product.mrp > product.price && <span className="text-lg text-[#B07A5C] line-through">{inr(product.mrp)}</span>}
            {/Pc|piece/i.test(product.weight || "") && <span className="text-sm text-[#B07A5C] font-medium">/ per piece</span>}
            <span className="text-xs text-[#7a9a5b] font-medium">Incl. all taxes</span>
          </div>

          <p className="text-[#6B4226]/80 mt-4 leading-relaxed">{product.description}</p>

          {/* Flavor */}
          <div className="mt-6">
            <label className="text-sm font-semibold text-[#6B4226]">Flavour</label>
            <div className="flex flex-wrap gap-2 mt-2">
              {FLAVORS.map((f) => (
                <button key={f} onClick={() => setFlavor(f)} className={`px-4 py-2 rounded-full text-sm border transition-colors ${flavor === f ? "bg-[#6B4226] text-white border-[#6B4226]" : "bg-white text-[#6B4226] border-[#EADBCB] hover:border-[#D46A8A]"}`}>{f}</button>
              ))}
            </div>
          </div>

          {/* Weight */}
          <div className="mt-5">
            <label className="text-sm font-semibold text-[#6B4226]">Weight</label>
            <div className="flex flex-wrap gap-2 mt-2">
              {WEIGHTS.map((w) => (
                <button key={w} onClick={() => setWeight(w)} className={`px-4 py-2 rounded-full text-sm border transition-colors ${weight === w ? "bg-[#6B4226] text-white border-[#6B4226]" : "bg-white text-[#6B4226] border-[#EADBCB] hover:border-[#D46A8A]"}`}>{w}</button>
              ))}
            </div>
          </div>

          {/* Message */}
          <div className="mt-5">
            <label className="text-sm font-semibold text-[#6B4226]">Message on cake (optional)</label>
            <input value={message} onChange={(e) => setMessage(e.target.value)} maxLength={40} placeholder="e.g. Happy Birthday Aisha!" className="w-full mt-2 h-11 px-4 rounded-xl bg-white border border-[#EADBCB] text-sm outline-none focus:border-[#D46A8A]" />
          </div>

          {/* Qty + actions */}
          <div className="flex items-center gap-4 mt-6">
            <div className="flex items-center gap-2 bg-white border border-[#EADBCB] rounded-full px-2">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-9 h-9 grid place-items-center text-[#6B4226]"><Minus size={16} /></button>
              <span className="w-6 text-center font-medium">{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} className="w-9 h-9 grid place-items-center text-[#6B4226]"><Plus size={16} /></button>
            </div>
            <button onClick={handleAdd} className="flex-1 h-12 rounded-full bg-white border-2 border-[#D46A8A] text-[#D46A8A] font-medium hover:bg-[#F7DCE4] transition-colors inline-flex items-center justify-center gap-2"><ShoppingBag size={18} /> Add to Cart</button>
          </div>
          <button onClick={handleBuyNow} className="w-full mt-3 h-12 rounded-full bg-[#D46A8A] text-white font-medium hover:bg-[#c25a7b] transition-colors">Buy Now</button>

          {/* Assurances */}
          <div className="grid grid-cols-3 gap-3 mt-7">
            {[{ icon: Clock, t: `Delivery: Tomorrow` }, { icon: Leaf, t: "Eggless option" }, { icon: ShieldCheck, t: "100% fresh" }].map((a, i) => (
              <div key={i} className="bg-[#FCEFE3] rounded-xl p-3 text-center">
                <a.icon size={20} className="text-[#D46A8A] mx-auto mb-1" />
                <div className="text-xs text-[#6B4226]">{a.t}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Reviews */}
      <section className="mt-16">
        <h2 className="font-display text-2xl font-bold text-[#3A2417] mb-6">Customer Reviews</h2>
        <div className="grid md:grid-cols-3 gap-5">
          {TESTIMONIALS.slice(0, 3).map((t) => (
            <div key={t.id} className="bg-white rounded-2xl p-5 bakery-shadow">
              <div className="flex gap-0.5 mb-2">{Array.from({ length: t.rating }).map((_, i) => <Star key={i} size={15} className="fill-[#E8A6B8] text-[#E8A6B8]" />)}</div>
              <p className="text-sm text-[#6B4226]">“{t.text}”</p>
              <div className="text-xs text-[#B07A5C] mt-3 font-medium">{t.name} · {t.city}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="mt-16">
          <h2 className="font-display text-2xl font-bold text-[#3A2417] mb-6">You may also love</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5 md:gap-7">{related.map((p) => <ProductCard key={p.id} product={p} />)}</div>
        </section>
      )}
    </div>
  );
};

export default ProductDetail;
